import { toLower, map, values } from 'lodash/fp';
import { createSelector } from 'reselect';
import { TextKey } from '@mews-commander/enums';
import { matchTimeUnitKey, MatchTimeUnitModifiers } from '@mews/framework';
import { ProductChargingMode, ProductPostingMode } from '@mews-commander/data-access';
import { Option as OptionType, ApplicationState } from '@mews-commander/types';
import { Option } from '@mews-commander/legacy-data-structures';
import { localizeText } from '@mews-commander/utils';
import {
    getProductChargingTextKeyForBookableServices,
    getProductPostingTextKeyForBookableServices,
} from '@mews/business-components';
import { getServiceData, isDayUseService } from '../ducks/services';

const createServiceTimeUnitKeySelector = (modifiers: MatchTimeUnitModifiers = {}) =>
    createSelector(isDayUseService, getServiceData, (isServiceDayUse, { timeUnit }) =>
        matchTimeUnitKey({
            ...modifiers,
            timeUnit,
            isServiceDayUse,
        })
    );

export const createServiceTimeUnitLocalizationSelector = (modifiers: MatchTimeUnitModifiers = {}) =>
    createSelector(createServiceTimeUnitKeySelector(modifiers), (key) => {
        const localizedText = localizeText(key as Mews.TextKey);
        return modifiers.perTimeUnit ? toLower(localizedText) : localizedText;
    });

export const getServiceTimeUnitPluralKey = createServiceTimeUnitKeySelector({ plural: true });
export const getServiceTimeUnitSingularKey = createServiceTimeUnitKeySelector({ plural: false });
export const getServiceTimeUnitPluralLocalization = createServiceTimeUnitLocalizationSelector({ plural: true });
export const getServicePerTimeUnitKey = createServiceTimeUnitKeySelector({ perTimeUnit: true });
export const getServicePerTimeUnitLocalization = createServiceTimeUnitLocalizationSelector({ perTimeUnit: true });

export const getServiceTimeUnitEnumOptions = <T extends string | number>(
    enumValues: T[],
    textKeyHandlerFactory: (value: T, perTimeUnitKey: Mews.TextKey) => Mews.TextKey
) =>
    createSelector(getServicePerTimeUnitKey, getServicePerTimeUnitLocalization, (perTimeUnitKey, perTimeUnitText) => {
        const options = map<T, OptionType[]>((enumValue) => {
            const textKey = enumValue && perTimeUnitKey ? textKeyHandlerFactory(enumValue, perTimeUnitKey) : null;
            return Option({
                value: enumValue,
                label: localizeText(textKey as Mews.TextKey, { timeUnit: perTimeUnitText }),
            });
        })(enumValues);
        return options;
    });

export const getBookableServiceChargingOptions = getServiceTimeUnitEnumOptions(
    values(ProductChargingMode),
    getProductChargingTextKeyForBookableServices
);

export const getBookableServicePostingOptions = getServiceTimeUnitEnumOptions(
    values(ProductPostingMode),
    getProductPostingTextKeyForBookableServices
);

const getAdditionalServiceOptions =
    (textKey: Mews.TextKey) =>
    (state: ApplicationState, { value }: { value: ProductChargingMode.Once | ProductPostingMode.Once }) => {
        return [Option({ value, label: localizeText(textKey) })];
    };

export const getAdditionalServiceChargingOptions = getAdditionalServiceOptions(TextKey.ProductChargingOnce);

export const getAdditionalServicePostingOptions = getAdditionalServiceOptions(TextKey.ProductPostingModeOnce);
